import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { Spinner, Text, tokens } from '@fluentui/react-components';
import { connectionsService } from '../api/connectionsService';
import {
  ADMIN_CONSENT_STATE_STORAGE_KEY,
  matchesAdminConsentState,
} from '../utils/adminConsentState';

/**
 * OAuth callback page — handles the redirect from the OAuth provider.
 * Forwards provider-specific query parameters to the API callback. Only the
 * Application Insights administrator-consent callback is unauthenticated.
 */
const OAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { connector: connectorParam } = useParams<{ connector: string }>();
  const [error, setError] = useState<string | null>(null);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    const connector = connectorParam || searchParams.get('connector') || undefined;

    const returnedState = searchParams.get('state');
    if (!connector || !returnedState) {
      setError('Missing callback parameters.');
      return;
    }

    if (connector === 'application-insights') {
      const expectedState = sessionStorage.getItem(ADMIN_CONSENT_STATE_STORAGE_KEY);
      if (!matchesAdminConsentState(expectedState, returnedState)) {
        sessionStorage.removeItem(ADMIN_CONSENT_STATE_STORAGE_KEY);
        setError('Administrator consent could not be matched to this browser.');
        return;
      }
    }

    connectionsService
      .handleOAuthCallback(connector, searchParams)
      .then((result) => {
        if (connector === 'application-insights') {
          sessionStorage.removeItem(ADMIN_CONSENT_STATE_STORAGE_KEY);
          setComplete(true);
          return;
        }
        if (!result.connectionId) throw new Error('OAuth callback did not identify a connection');
        navigate(`/data-sources/${result.connectionId}`, { replace: true });
      })
      .catch((err) => {
        setError(err.message || 'OAuth authentication failed.');
      });
  }, [searchParams, navigate, connectorParam]);

  if (complete) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Text size={500} weight="semibold">Administrator consent flow completed</Text>
        <Text size={300} style={{ marginTop: 12, display: 'block' }}>
          The requester can return to the original browser to verify access and finish setup.
        </Text>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Text size={500} weight="semibold" style={{ color: tokens.colorPaletteRedForeground1 }}>
          Authentication Failed
        </Text>
        <br />
        <Text size={300} style={{ marginTop: 12, display: 'block' }}>{error}</Text>
        <br />
        <a
          href={connectorParam === 'application-insights'
            ? '/data-sources/new/connect?connector=application_insights'
            : '/data-sources'}
          style={{ marginTop: 16, display: 'inline-block' }}
        >
          {connectorParam === 'application-insights' ? 'Return to setup' : 'Back to Data Sources'}
        </a>
      </div>
    );
  }

  return (
    <div style={{ padding: 80, textAlign: 'center' }}>
      <Spinner size="large" label="Completing authentication…" />
    </div>
  );
};

export default OAuthCallbackPage;